import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";

import {
  MAP_STYLE,
  EARTHQUAKE_HAZARD_COLOR_STOPS,
  EARTHQUAKE_HAZARD_SOURCE_URL,
  EARTHQUAKE_TECTONIC_SOURCE_URL,
  EARTHQUAKE_FAULTS_SOURCE_URL,
  earthquakeHazardFillLayer,
  earthquakeTectonicPlatesLayer,
  earthquakeActiveFaultsLayer,
  fireGenericLayer,
  FIRE_TILES_HIGH_ZOOM,
  FIRE_TILES_LOW_ZOOM,
  MAPBOX_TILES,
  floodLakesLayer,
  floodHazardLayer,
  FLOOD_HAZARD_TILESETS,
  FLOOD_HAZARD_COLOR_STOPS_Z13,
  buildFloodHazardFillColor,
  floodRiversLayer,
} from "../../constants";
import { getFireBufferFeatureCollection } from "../../markers/FireBufferPolygons/fireBufferPolygons";

const SNAPSHOT_WIDTH = 900;
const SNAPSHOT_HEIGHT = 480;
const SNAPSHOT_ZOOM = 14;
const POINT_SOURCE_ID = "assessment-point";
const POINT_LAYER_ID = "assessment-point-layer";
const FIRE_CIRCLE_SOURCE_ID = "pdf-circle-source";
const FIRE_CIRCLE_FILL_ID = "pdf-circle-fill";
const FIRE_CIRCLE_OUTLINE_ID = "pdf-circle-outline";
const FIRE_BUFFER_SOURCE_ID = "pdf-fire-buffer-source";
const FIRE_BUFFER_FILL_ID = "pdf-fire-buffer-fill";
const FIRE_BUFFER_OUTLINE_ID = "pdf-fire-buffer-outline";
const LEGEND_MARGIN_LEFT = 14;
const LEGEND_MARGIN_BOTTOM = 14;
const LEGEND_PADDING = 10;
const LEGEND_WIDTH = 270;
const LEGEND_TITLE_HEIGHT = 16;
const LEGEND_ITEM_HEIGHT = 16;
const LEGEND_COLOR_SIZE = 8;
const LEGEND_TEXT_OFFSET_Y = 7;
const EARTHQUAKE_DEFAULT_LAYERS = ["hazard"];
const EARTHQUAKE_LAYER_KEYS = new Set([
  "hazard",
  "tectonicPlates",
  "activeFaults",
]);
const EARTHQUAKE_HAZARD_SOURCE_ID = "pdf-earthquake-hazard";
const EARTHQUAKE_HAZARD_LAYER_ID = "pdf-earthquake-hazard-layer";
const EARTHQUAKE_TECTONIC_SOURCE_ID = "pdf-earthquake-tectonic";
const EARTHQUAKE_TECTONIC_LAYER_ID = "pdf-earthquake-tectonic-layer";
const EARTHQUAKE_FAULTS_SOURCE_ID = "pdf-earthquake-faults";
const EARTHQUAKE_FAULTS_LAYER_ID = "pdf-earthquake-faults-layer";

const FIRE_TERRAIN_LEGEND = {
  title: "Terrain Classifications in Greece",
  colors: [
    "#228B22",
    "#804000",
    "#CCF24D",
    "#00FF08",
    "#80FF00",
    "#FFFF80",
    "#FF80FF",
    "#FF0000",
    "#BFBFBF",
    "#0080FF",
    "#00FFFF",
  ],
  labels: [
    "Woody needle leaved trees (Conifer)",
    "Low-growing woody plants (Shrub)",
    "Permanent herbaceous (Grass)",
    "Woody broadleaved evergreen trees",
    "Woody broadleaved deciduous trees",
    "Periodically herbaceous (Crops)",
    "Lichens and mosses",
    "Sealed",
    "Non and sparsely vegetated",
    "Water",
    "Snow and ice",
  ],
};

const FLOOD_LEGEND = {
  title: "Flood zones in Greece",
  colors: FLOOD_HAZARD_COLOR_STOPS_Z13.map(([, color]) => color),
  labels: ["Very low", "Low", "Medium", "High", "Very high"],
};

const EARTHQUAKE_LEGEND = {
  title: "Seismic zones in Greece",
  colors: EARTHQUAKE_HAZARD_COLOR_STOPS.map(([, color]) => color),
  labels: ["Very low", "Low", "Moderate", "High", "Very high"],
};

function createHiddenMapContainer() {
  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-10000px";
  wrapper.style.top = "0";
  wrapper.style.width = `${SNAPSHOT_WIDTH}px`;
  wrapper.style.height = `${SNAPSHOT_HEIGHT}px`;
  wrapper.style.pointerEvents = "none";
  wrapper.style.opacity = "0";
  wrapper.style.overflow = "hidden";
  wrapper.style.backgroundColor = "transparent";

  const mapContainer = document.createElement("div");
  mapContainer.style.position = "absolute";
  mapContainer.style.inset = "0";
  mapContainer.style.width = "100%";
  mapContainer.style.height = "100%";

  wrapper.appendChild(mapContainer);
  document.body.appendChild(wrapper);

  return { wrapper, mapContainer };
}

function waitForMapIdle(map, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    let timeoutId = null;

    const onIdle = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      resolve();
    };

    timeoutId = setTimeout(() => {
      map.off("idle", onIdle);
      reject(new Error("Map snapshot timed out."));
    }, timeoutMs);

    map.once("idle", onIdle);
  });
}

function addCenterPointLayer(map, longitude, latitude) {
  map.addSource(POINT_SOURCE_ID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [longitude, latitude],
          },
          properties: {},
        },
      ],
    },
  });

  map.addLayer({
    id: POINT_LAYER_ID,
    type: "circle",
    source: POINT_SOURCE_ID,
    paint: {
      "circle-radius": 7,
      "circle-color": "#f43434",
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 2,
    },
  });
}

function addAreaPolygonLayers(map, geometry) {
  map.addSource("pdf-area-source", {
    type: "geojson",
    data: geometry,
  });
  map.addLayer({
    id: "pdf-area-fill",
    type: "fill",
    source: "pdf-area-source",
    paint: { "fill-color": "#4eb3f2", "fill-opacity": 0.16 },
  });
  map.addLayer({
    id: "pdf-area-line",
    type: "line",
    source: "pdf-area-source",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#1f8fe0", "line-width": 3 },
  });
}

function addFireLayers(map) {
  FIRE_TILES_HIGH_ZOOM.forEach((tileset, index) => {
    const sourceId = `pdf-fire-high-${index}`;
    const layerId = `pdf-fire-high-layer-${index}`;
    map.addSource(sourceId, {
      type: "vector",
      url: tileset.url,
    });
    map.addLayer({
      ...fireGenericLayer,
      id: layerId,
      source: sourceId,
      "source-layer": tileset["source-layer"],
      minzoom: 11,
    });
  });

  FIRE_TILES_LOW_ZOOM.forEach((tileset, index) => {
    const sourceId = `pdf-fire-low-${index}`;
    const layerId = `pdf-fire-low-layer-${index}`;
    map.addSource(sourceId, {
      type: "vector",
      url: tileset.url,
    });
    map.addLayer({
      ...fireGenericLayer,
      id: layerId,
      source: sourceId,
      "source-layer": tileset["source-layer"],
      maxzoom: 11,
    });
  });
}

function addFireCircleLayers(map, longitude, latitude) {
  const circleGeojson = turf.circle([longitude, latitude], 0.5, {
    steps: 64,
    units: "kilometers",
  });

  map.addSource(FIRE_CIRCLE_SOURCE_ID, {
    type: "geojson",
    data: circleGeojson,
  });

  map.addLayer({
    id: FIRE_CIRCLE_FILL_ID,
    type: "fill",
    source: FIRE_CIRCLE_SOURCE_ID,
    paint: {
      "fill-color": "black",
      "fill-opacity": 0.15,
    },
  });

  map.addLayer({
    id: FIRE_CIRCLE_OUTLINE_ID,
    type: "line",
    source: FIRE_CIRCLE_SOURCE_ID,
    paint: {
      "line-color": "#4eb3f2",
      "line-width": 4,
    },
  });
}

function addFireBufferLayers(map, bufferGeojson) {
  map.addSource(FIRE_BUFFER_SOURCE_ID, {
    type: "geojson",
    data: bufferGeojson,
  });

  map.addLayer({
    id: FIRE_BUFFER_FILL_ID,
    type: "fill",
    source: FIRE_BUFFER_SOURCE_ID,
    paint: {
      "fill-color": "black",
      "fill-opacity": 0.15,
    },
  });

  map.addLayer({
    id: FIRE_BUFFER_OUTLINE_ID,
    type: "line",
    source: FIRE_BUFFER_SOURCE_ID,
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#4eb3f2",
      "line-width": 3,
    },
  });
}

function addFloodLayers(map) {
  map.addSource("pdf-flood-greece", {
    type: "vector",
    url: MAPBOX_TILES.greece,
  });

  FLOOD_HAZARD_TILESETS.forEach((tileset) => {
    map.addSource(`pdf-${tileset.id}`, {
      type: "vector",
      url: tileset.url,
    });
    map.addLayer({
      ...floodHazardLayer,
      id: `pdf-${tileset.id}-layer`,
      source: `pdf-${tileset.id}`,
      minzoom: tileset.minzoom,
      maxzoom: tileset.maxzoom,
      paint: {
        ...floodHazardLayer.paint,
        "fill-color": buildFloodHazardFillColor(
          tileset.property,
          tileset.colorStops,
        ),
        ...(tileset.useTransparentOutline
          ? { "fill-outline-color": "rgba(0, 0, 0, 0)" }
          : {}),
      },
    });
  });

  map.addSource("pdf-flood-rivers", {
    type: "vector",
    url: MAPBOX_TILES.rivers,
  });
  map.addLayer({
    ...floodRiversLayer,
    id: "pdf-flood-rivers-layer",
    source: "pdf-flood-rivers",
  });

  map.addSource("pdf-flood-lakes", {
    type: "vector",
    url: MAPBOX_TILES.lakes,
  });
  map.addLayer({
    ...floodLakesLayer,
    id: "pdf-flood-lakes-layer",
    source: "pdf-flood-lakes",
  });
}

function normalizeEarthquakeLayers(earthquakeLayers) {
  if (!Array.isArray(earthquakeLayers) || earthquakeLayers.length === 0) {
    return EARTHQUAKE_DEFAULT_LAYERS;
  }

  const normalized = earthquakeLayers.filter((layer) =>
    EARTHQUAKE_LAYER_KEYS.has(layer),
  );
  return normalized.length > 0 ? normalized : EARTHQUAKE_DEFAULT_LAYERS;
}

function addEarthquakeLayers(map, earthquakeLayers) {
  const selectedLayers = normalizeEarthquakeLayers(earthquakeLayers);

  if (selectedLayers.includes("hazard")) {
    map.addSource(EARTHQUAKE_HAZARD_SOURCE_ID, {
      type: "vector",
      url: EARTHQUAKE_HAZARD_SOURCE_URL,
    });
    map.addLayer({
      ...earthquakeHazardFillLayer,
      id: EARTHQUAKE_HAZARD_LAYER_ID,
      source: EARTHQUAKE_HAZARD_SOURCE_ID,
    });
  }

  if (selectedLayers.includes("tectonicPlates")) {
    map.addSource(EARTHQUAKE_TECTONIC_SOURCE_ID, {
      type: "vector",
      url: EARTHQUAKE_TECTONIC_SOURCE_URL,
    });
    map.addLayer({
      ...earthquakeTectonicPlatesLayer,
      id: EARTHQUAKE_TECTONIC_LAYER_ID,
      source: EARTHQUAKE_TECTONIC_SOURCE_ID,
    });
  }

  if (selectedLayers.includes("activeFaults")) {
    map.addSource(EARTHQUAKE_FAULTS_SOURCE_ID, {
      type: "vector",
      url: EARTHQUAKE_FAULTS_SOURCE_URL,
    });
    map.addLayer({
      ...earthquakeActiveFaultsLayer,
      id: EARTHQUAKE_FAULTS_LAYER_ID,
      source: EARTHQUAKE_FAULTS_SOURCE_ID,
    });
  }
}

function addRiskLayers(map, riskType, options = {}) {
  if (riskType === "fire") {
    addFireLayers(map);
    return;
  }

  if (riskType === "flood") {
    addFloodLayers(map);
    return;
  }

  if (riskType === "earthquake") {
    addEarthquakeLayers(map, options.earthquakeLayers);
  }
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Failed to load map snapshot image."));
    image.src = dataUrl;
  });
}

function getLegendConfig(riskType, legendLabels = {}) {
  if (riskType === "fire") {
    // For fire screenshot we intentionally use terrain legend (not hazard).
    return {
      ...FIRE_TERRAIN_LEGEND,
      title: legendLabels?.fire?.title || FIRE_TERRAIN_LEGEND.title,
      labels: legendLabels?.fire?.labels || FIRE_TERRAIN_LEGEND.labels,
    };
  }
  if (riskType === "flood") {
    return {
      ...FLOOD_LEGEND,
      title: legendLabels?.flood?.title || FLOOD_LEGEND.title,
      colors: legendLabels?.flood?.colors || FLOOD_LEGEND.colors,
      labels: legendLabels?.flood?.labels || FLOOD_LEGEND.labels,
    };
  }
  return {
    ...EARTHQUAKE_LEGEND,
    title: legendLabels?.earthquake?.title || EARTHQUAKE_LEGEND.title,
    colors: legendLabels?.earthquake?.colors || EARTHQUAKE_LEGEND.colors,
    labels: legendLabels?.earthquake?.labels || EARTHQUAKE_LEGEND.labels,
    selectedLayers: legendLabels?.earthquake?.selectedLayers || {},
  };
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

function drawEarthquakeLegendBox(ctx, legend, earthquakeLayers) {
  const selectedLayers = normalizeEarthquakeLayers(earthquakeLayers);
  const selectedOverlayLayers = selectedLayers.filter(
    (layer) => layer !== "hazard",
  );
  const drawHazardBar = selectedLayers.includes("hazard");
  const hazardBarHeight = drawHazardBar ? 30 : 0;
  const selectedLayersHeight =
    selectedOverlayLayers.length * LEGEND_ITEM_HEIGHT;
  const legendHeight =
    LEGEND_PADDING * 2 +
    LEGEND_TITLE_HEIGHT +
    hazardBarHeight +
    selectedLayersHeight;
  const legendX = LEGEND_MARGIN_LEFT;
  const legendY = SNAPSHOT_HEIGHT - LEGEND_MARGIN_BOTTOM - legendHeight;
  const legendInnerWidth = LEGEND_WIDTH - LEGEND_PADDING * 2;

  drawRoundedRect(ctx, legendX, legendY, LEGEND_WIDTH, legendHeight, 8);
  ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
  ctx.fill();
  ctx.strokeStyle = "rgba(0, 0, 0, 0.15)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#212121";
  ctx.font = "bold 10px Arial";
  ctx.fillText(
    legend.title,
    legendX + LEGEND_PADDING,
    legendY + LEGEND_PADDING + 8,
  );

  let cursorY = legendY + LEGEND_PADDING + LEGEND_TITLE_HEIGHT + 3;

  if (drawHazardBar) {
    const barHeight = 10;
    const segmentWidth = legendInnerWidth / legend.colors.length;
    legend.colors.forEach((color, index) => {
      ctx.fillStyle = color;
      ctx.fillRect(
        legendX + LEGEND_PADDING + index * segmentWidth,
        cursorY,
        segmentWidth,
        barHeight,
      );
    });

    cursorY += barHeight + 11;
    ctx.fillStyle = "#2d2d2d";
    ctx.font = "8px Arial";
    legend.labels.forEach((label, index) => {
      const labelCenterX =
        legendX + LEGEND_PADDING + segmentWidth * index + segmentWidth / 2;
      const truncatedLabel =
        label.length > 12 ? `${label.slice(0, 11).trim()}...` : label;
      const textWidth = ctx.measureText(truncatedLabel).width;
      ctx.fillText(truncatedLabel, labelCenterX - textWidth / 2, cursorY);
    });
    cursorY += 5;
  }

  const layerStyles = {
    hazard: { color: legend.colors[2] || legend.colors[0], type: "fill" },
    tectonicPlates: { color: "#240566", type: "line" },
    activeFaults: {
      type: "line-multi",
      colors: ["hsl(113, 52%, 48%)", "hsl(39, 93%, 47%)", "hsl(0, 89%, 53%)"],
    },
  };

  ctx.font = "9px Arial";
  selectedOverlayLayers.forEach((layerKey, index) => {
    const rowY = cursorY + index * LEGEND_ITEM_HEIGHT;
    const style = layerStyles[layerKey] || layerStyles.hazard;

    if (style.type === "line-multi") {
      const xStart = legendX + LEGEND_PADDING;
      const y = rowY + LEGEND_TEXT_OFFSET_Y;
      const segmentGap = 2;
      const totalWidth = LEGEND_COLOR_SIZE + 10;
      const segmentWidth =
        (totalWidth - segmentGap * (style.colors.length - 1)) /
        style.colors.length;
      let x = xStart;

      style.colors.forEach((lineColor) => {
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + segmentWidth, y);
        ctx.stroke();
        x += segmentWidth + segmentGap;
      });
    } else if (style.type === "line") {
      ctx.strokeStyle = style.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(legendX + LEGEND_PADDING, rowY + LEGEND_TEXT_OFFSET_Y);
      ctx.lineTo(
        legendX + LEGEND_PADDING + LEGEND_COLOR_SIZE + 6,
        rowY + LEGEND_TEXT_OFFSET_Y,
      );
      ctx.stroke();
    } else {
      ctx.fillStyle = style.color;
      ctx.fillRect(
        legendX + LEGEND_PADDING,
        rowY + 2,
        LEGEND_COLOR_SIZE + 2,
        LEGEND_COLOR_SIZE + 2,
      );
    }

    ctx.fillStyle = "#2d2d2d";
    ctx.fillText(
      legend.selectedLayers[layerKey] || layerKey,
      legendX + LEGEND_PADDING + 18,
      rowY + LEGEND_TEXT_OFFSET_Y + 1,
    );
  });
}

// Horizontal gradient bar with labels spread beneath it (mirrors the in-app
// horizontal LegendBar). Used for hazard ramps such as flood.
function drawHorizontalLegendBox(ctx, legend) {
  const barHeight = 10;
  const legendHeight =
    LEGEND_PADDING * 2 + LEGEND_TITLE_HEIGHT + barHeight + 16;
  const legendX = LEGEND_MARGIN_LEFT;
  const legendY = SNAPSHOT_HEIGHT - LEGEND_MARGIN_BOTTOM - legendHeight;
  const legendInnerWidth = LEGEND_WIDTH - LEGEND_PADDING * 2;

  drawRoundedRect(ctx, legendX, legendY, LEGEND_WIDTH, legendHeight, 8);
  ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
  ctx.fill();
  ctx.strokeStyle = "rgba(0, 0, 0, 0.15)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#212121";
  ctx.font = "bold 10px Arial";
  ctx.fillText(
    legend.title,
    legendX + LEGEND_PADDING,
    legendY + LEGEND_PADDING + 8,
  );

  const barY = legendY + LEGEND_PADDING + LEGEND_TITLE_HEIGHT + 3;
  const segmentWidth = legendInnerWidth / legend.colors.length;
  legend.colors.forEach((color, index) => {
    ctx.fillStyle = color;
    ctx.fillRect(
      legendX + LEGEND_PADDING + index * segmentWidth,
      barY,
      segmentWidth,
      barHeight,
    );
  });

  const labelsY = barY + barHeight + 9;
  ctx.fillStyle = "#2d2d2d";
  ctx.font = "8px Arial";
  const labelSegmentWidth = legendInnerWidth / legend.labels.length;
  legend.labels.forEach((label, index) => {
    const labelCenterX =
      legendX +
      LEGEND_PADDING +
      labelSegmentWidth * index +
      labelSegmentWidth / 2;
    const truncatedLabel =
      label.length > 12 ? `${label.slice(0, 11).trim()}...` : label;
    const textWidth = ctx.measureText(truncatedLabel).width;
    ctx.fillText(truncatedLabel, labelCenterX - textWidth / 2, labelsY);
  });
}

function drawLegendBox(ctx, riskType, legendLabels, earthquakeLayers) {
  const legend = getLegendConfig(riskType, legendLabels);
  if (riskType === "earthquake") {
    drawEarthquakeLegendBox(ctx, legend, earthquakeLayers);
    return;
  }

  if (riskType === "flood") {
    drawHorizontalLegendBox(ctx, legend);
    return;
  }

  const itemCount = legend.labels.length;
  const legendHeight =
    LEGEND_PADDING * 2 + LEGEND_TITLE_HEIGHT + itemCount * LEGEND_ITEM_HEIGHT;
  const legendX = LEGEND_MARGIN_LEFT;
  const legendY = SNAPSHOT_HEIGHT - LEGEND_MARGIN_BOTTOM - legendHeight;

  drawRoundedRect(ctx, legendX, legendY, LEGEND_WIDTH, legendHeight, 8);
  ctx.fillStyle = "rgba(255, 255, 255, 0.96)";
  ctx.fill();
  ctx.strokeStyle = "rgba(0, 0, 0, 0.15)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#212121";
  ctx.font = "bold 10px Arial";
  ctx.fillText(
    legend.title,
    legendX + LEGEND_PADDING,
    legendY + LEGEND_PADDING + 8,
  );

  legend.labels.forEach((label, index) => {
    const itemY =
      legendY +
      LEGEND_PADDING +
      LEGEND_TITLE_HEIGHT +
      index * LEGEND_ITEM_HEIGHT +
      2;
    ctx.fillStyle =
      legend.colors[index] || legend.colors[legend.colors.length - 1];
    ctx.fillRect(
      legendX + LEGEND_PADDING,
      itemY + 2,
      LEGEND_COLOR_SIZE,
      LEGEND_COLOR_SIZE,
    );

    ctx.fillStyle = "#2d2d2d";
    ctx.font = "9px Arial";
    ctx.fillText(
      label,
      legendX + LEGEND_PADDING + 14,
      itemY + LEGEND_TEXT_OFFSET_Y,
    );
  });
}

async function drawLegendOverlay(
  snapshotDataUrl,
  riskType,
  legendLabels,
  earthquakeLayers,
) {
  const image = await loadImage(snapshotDataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = SNAPSHOT_WIDTH;
  canvas.height = SNAPSHOT_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return snapshotDataUrl;
  }

  ctx.drawImage(image, 0, 0, SNAPSHOT_WIDTH, SNAPSHOT_HEIGHT);
  drawLegendBox(ctx, riskType, legendLabels, earthquakeLayers);
  return canvas.toDataURL("image/png");
}

async function captureRiskSnapshot({
  riskType,
  latitude,
  longitude,
  mapboxToken,
  mapStyle = MAP_STYLE.street,
  legendLabels,
  earthquakeLayers,
  geometry = null,
  fire = null,
}) {
  const isArea = Boolean(geometry);
  if (!mapboxToken) {
    throw new Error("Missing Mapbox token for PDF map snapshots.");
  }

  const { wrapper, mapContainer } = createHiddenMapContainer();
  const map = new mapboxgl.Map({
    container: mapContainer,
    accessToken: mapboxToken,
    style: mapStyle,
    center: [longitude, latitude],
    zoom: SNAPSHOT_ZOOM,
    // Match the live map (flat mercator, no 3D buildings) so the snapshot
    // looks like what the user sees on screen.
    projection: "mercator",
    interactive: false,
    attributionControl: false,
    preserveDrawingBuffer: true,
  });

  try {
    await new Promise((resolve, reject) => {
      map.once("load", resolve);
      map.once("error", (event) => {
        reject(event?.error || new Error("Failed to initialize map snapshot."));
      });
    });

    addRiskLayers(map, riskType, { earthquakeLayers });
    if (isArea) {
      // Area assessment — draw the polygons and frame the map to their bounds
      // instead of dropping a point marker.
      // Fire risk is assessed over a buffer around each polygon; draw it under
      // the polygons so the blue polygon fills stay crisp on top.
      if (riskType === "fire") {
        const bufferGeojson = getFireBufferFeatureCollection(fire);
        if (bufferGeojson) {
          addFireBufferLayers(map, bufferGeojson);
        }
      }
      addAreaPolygonLayers(map, geometry);
      try {
        const [minX, minY, maxX, maxY] = turf.bbox(geometry);
        map.fitBounds(
          [
            [minX, minY],
            [maxX, maxY],
          ],
          { padding: 100, maxZoom: 17, animate: false },
        );
      } catch {
        /* fall back to the centroid center set at init */
      }
    } else {
      if (riskType === "fire") {
        addFireCircleLayers(map, longitude, latitude);
      }
      addCenterPointLayer(map, longitude, latitude);
    }
    await waitForMapIdle(map);

    const mapDataUrl = map.getCanvas().toDataURL("image/png");
    return drawLegendOverlay(
      mapDataUrl,
      riskType,
      legendLabels,
      earthquakeLayers,
    );
  } finally {
    map.remove();
    wrapper.remove();
  }
}

export async function captureAssessmentRiskSnapshots({
  latitude,
  longitude,
  mapboxToken,
  mapStyle,
  legendLabels,
  earthquakeLayers,
  geometry = null,
  fire = null,
}) {
  const snapshots = {};
  const riskTypes = ["fire", "flood", "earthquake"];

  for (const riskType of riskTypes) {
    snapshots[riskType] = await captureRiskSnapshot({
      riskType,
      latitude,
      longitude,
      mapboxToken,
      mapStyle,
      legendLabels,
      earthquakeLayers,
      geometry,
      fire,
    });
  }

  return snapshots;
}
